import { MEDIA_TYPE } from '../base/media/constants';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { isLocalTrackMuted } from '../base/tracks/functions.any';
import { getElectronGlobalNS } from '../base/util/helpers';
import { isScreenVideoShared } from '../screen-share/functions';
import { enterDocumentPiP } from './actions';
import { isDocumentPiPSupported } from './external-api.shared';
import { enterPiP, requestPictureInPicture, shouldShowPiP, updateMediaSessionState } from './functions';
import logger from './logger';
/**
 * Listens to audio and video mute state changes when PiP is active
 * and updates the MediaSession API to reflect the current state in PiP controls.
 */
StateListenerRegistry.register(
/* selector */ (state) => {
    // Skip if PiP is disabled or shouldn't be shown (e.g., on prejoin without showOnPrejoin).
    if (!shouldShowPiP(state)) {
        return null;
    }
    const isPiPActive = state['features/pip']?.isPiPActive;
    if (!isPiPActive) {
        return null;
    }
    return {
        audioMuted: isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO),
        videoMuted: isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO)
    };
}, 
/* listener */ (muteState) => {
    if (muteState === null) {
        return;
    }
    updateMediaSessionState({
        cameraActive: !muteState.videoMuted,
        microphoneActive: !muteState.audioMuted
    });
}, {
    deepEquals: true
});
StateListenerRegistry.register(
/* selector */ shouldShowPiP, 
/* listener */ (_shouldShowPiP) => {
    const electronNS = getElectronGlobalNS();
    if (_shouldShowPiP) {
        // Expose requestPictureInPicture for Electron main process.
        if (!electronNS.requestPictureInPicture) {
            logger.debug('Exposing requestPictureInPicture to Electron namespace');
            electronNS.requestPictureInPicture = requestPictureInPicture;
        }
    }
    else if (typeof electronNS.requestPictureInPicture === 'function') {
        logger.debug('Removing requestPictureInPicture from Electron namespace (PiP disabled)');
        delete electronNS.requestPictureInPicture;
    }
});
/**
 * Registers the MediaSession `enterpictureinpicture` action handler when PiP is
 * available and the browser supports the Document PiP API. Chromium calls this
 * handler automatically when the user switches tab, so the rich PiP window opens
 * with no extra gesture — exactly like Google Meet.
 */
StateListenerRegistry.register(
/* selector */ (state) => shouldShowPiP(state) && isDocumentPiPSupported(), 
/* listener */ (enabled, store) => {
    // @ts-ignore - mediaSession is not fully typed in all environments.
    if (!('mediaSession' in navigator) || !navigator.mediaSession?.setActionHandler) {
        return;
    }
    try {
        // @ts-ignore - 'enterpictureinpicture' is a newer MediaSession action.
        navigator.mediaSession.setActionHandler('enterpictureinpicture', enabled
            ? () => store.dispatch(enterDocumentPiP())
            : null);
    }
    catch (error) {
        logger.warn('enterpictureinpicture MediaSession action not supported:', error);
    }
});
/**
 * Automatically enters Picture-in-Picture as soon as local screen sharing starts, instead of
 * waiting for the window to lose focus. Users start sharing precisely because they are about to
 * switch to the window/app being shared, so PiP should already be up by the time they do.
 */
StateListenerRegistry.register(
/* selector */ (state) => shouldShowPiP(state) && isScreenVideoShared(state), 
/* listener */ (screenSharingWithPiPAllowed, store) => {
    if (!screenSharingWithPiPAllowed || store.getState()['features/pip']?.isPiPActive) {
        return;
    }
    if (isDocumentPiPSupported()) {
        store.dispatch(enterDocumentPiP());
        return;
    }
    enterPiP(document.getElementById('pipVideo'));
});
